import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  PAYMENT_ACTION_LAYOUT,
  canChargePayment,
  canSendPaymentReceipt,
  getPaymentActionLayout,
  isPartialPayment,
} from '#/entities/shipment'
import type { ShipmentPaymentItem } from '#/entities/shipment'
import { SettlePaymentModal } from '#/features/settle-payment'
import { useCopyLink } from '#/shared/lib/clipboard'
import { Button } from '#/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/shared/ui/dropdown-menu'
import {
  ArchiveIcon,
  LinkChainIcon,
  MoreHorizontalIcon,
  NoteEditIcon,
  SendIcon,
} from '#/shared/ui/icons'
import { Tooltip } from '#/shared/ui/tooltip'

import { usePaymentActions } from '../model/use-payment-actions'
import type { PaymentActions } from '../model/use-payment-actions'
import { useSendInvoice } from '../model/use-send-invoice'

import { PaymentCommentDialog } from './payment-comment-dialog'
import { PaymentViewModal } from './payment-view-modal'

const ACTION_BUTTON_MIN_WIDTH = 72

function Spinner() {
  return (
    <span
      aria-hidden
      className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  )
}

function SendInvoiceButton({
  paymentId,
  shipmentId,
  disabled,
}: {
  paymentId: string
  shipmentId: string | undefined
  disabled: boolean
}) {
  const { t } = useTranslation()
  const { canSend, isSending, sendInvoice } = useSendInvoice(
    paymentId,
    shipmentId,
  )
  return (
    <Tooltip
      content={t('orderDetails.comment.payment.disabledReason.noCustomerEmail')}
      disabled={canSend}
    >
      <span className="inline-flex">
        <Button
          variant="outline"
          size="xs"
          pressEffect
          className="gap-2.5 px-2.5"
          disabled={disabled || isSending || !canSend}
          onClick={sendInvoice}
        >
          {isSending && <Spinner />}
          {t('orderDetails.comment.payment.sendInvoice')}
        </Button>
      </span>
    </Tooltip>
  )
}

function ViewButton({ onView }: { onView: () => void }) {
  const { t } = useTranslation()
  return (
    <Button
      variant="outline"
      size="xs"
      pressEffect
      className="gap-2.5 px-2.5"
      onClick={onView}
    >
      {t('orderDetails.comment.payment.view')}
    </Button>
  )
}

function ChargingBadge() {
  const { t } = useTranslation()
  return (
    <span className="inline-flex select-none items-center gap-2 rounded-md border border-gray-200 px-2.5 py-1.25 text-xs font-medium text-gray-500">
      <Spinner />
      {t('orderDetails.comment.payment.charging')}
    </span>
  )
}

interface ActionGroupProps {
  actions: PaymentActions
}

function CopyLinkItems({ item }: { item: ShipmentPaymentItem }) {
  const { t } = useTranslation()
  const copyLink = useCopyLink()
  const { invoiceLink, stripeInvoiceUrl } = item
  if (!invoiceLink && !stripeInvoiceUrl) return null
  return (
    <>
      {invoiceLink && (
        <DropdownMenuItem
          onSelect={() => {
            void copyLink(invoiceLink)
          }}
        >
          <LinkChainIcon className="size-4" />
          {t('orderDetails.comment.payment.copyInvoiceLink')}
        </DropdownMenuItem>
      )}
      {stripeInvoiceUrl && (
        <DropdownMenuItem
          onSelect={() => {
            void copyLink(stripeInvoiceUrl)
          }}
        >
          <LinkChainIcon className="size-4" />
          {t('orderDetails.comment.payment.copyStripeLink')}
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
    </>
  )
}

function PaymentMoreMenu({
  item,
  actions,
  shipmentId,
  onComment,
}: {
  item: ShipmentPaymentItem
  actions: PaymentActions
  shipmentId: string | undefined
  onComment: () => void
}) {
  const { t } = useTranslation()
  const invoice = useSendInvoice(item.id, shipmentId)
  const showSendInvoice = isPartialPayment(item)
  const showSendReceipt = canSendPaymentReceipt(item)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="xs"
          pressEffect
          aria-label={t('orderDetails.comment.payment.more')}
          disabled={actions.isPending}
          className="px-1.5"
        >
          <MoreHorizontalIcon className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {showSendInvoice && (
          <>
            <DropdownMenuItem
              disabled={!invoice.canSend || invoice.isSending}
              onSelect={() => {
                void invoice.sendInvoice()
              }}
            >
              <SendIcon className="size-4" />
              {t('orderDetails.comment.payment.sendInvoice')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {showSendReceipt && (
          <>
            <DropdownMenuItem
              disabled={!actions.canSend}
              onSelect={() => {
                void actions.sendReceipt()
              }}
            >
              <SendIcon className="size-4" />
              {t('orderDetails.comment.payment.sendReceipt')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <CopyLinkItems item={item} />
        <DropdownMenuItem onSelect={onComment}>
          <NoteEditIcon className="size-4" />
          {t('orderDetails.comment.payment.commentAction')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            void actions.archive()
          }}
        >
          <ArchiveIcon className="size-4" />
          {t('orderDetails.comment.payment.archive')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function SettleButton({
  actions,
  onSettle,
}: ActionGroupProps & { onSettle: () => void }) {
  const { t } = useTranslation()
  return (
    <Button
      size="xs"
      pressEffect
      className="gap-2.5 px-3.75"
      style={{ minWidth: ACTION_BUTTON_MIN_WIDTH }}
      disabled={actions.isPending}
      onClick={onSettle}
    >
      {t('orderDetails.comment.payment.settle')}
    </Button>
  )
}

function ActionGroup({
  item,
  actions,
  shipmentId,
  onSettle,
  onView,
}: {
  item: ShipmentPaymentItem
  actions: PaymentActions
  shipmentId: string | undefined
  onSettle: () => void
  onView: () => void
}) {
  switch (getPaymentActionLayout(item)) {
    case PAYMENT_ACTION_LAYOUT.viewOnly:
      return <ViewButton onView={onView} />
    case PAYMENT_ACTION_LAYOUT.receipt:
      return <ViewButton onView={onView} />
    case PAYMENT_ACTION_LAYOUT.charging:
      return (
        <>
          <SendInvoiceButton
            paymentId={item.id}
            shipmentId={shipmentId}
            disabled
          />
          <ChargingBadge />
        </>
      )
    case PAYMENT_ACTION_LAYOUT.retry:
      return (
        <>
          {canChargePayment(item) && (
            <SettleButton actions={actions} onSettle={onSettle} />
          )}
        </>
      )
    case PAYMENT_ACTION_LAYOUT.partial:
      return (
        <>
          <ViewButton onView={onView} />
          {canChargePayment(item) && (
            <SettleButton actions={actions} onSettle={onSettle} />
          )}
        </>
      )
    default:
      return (
        <>
          <SendInvoiceButton
            paymentId={item.id}
            shipmentId={shipmentId}
            disabled={actions.isPending}
          />
          {canChargePayment(item) && (
            <SettleButton actions={actions} onSettle={onSettle} />
          )}
        </>
      )
  }
}

export function PaymentRowActions({
  item,
  shipmentId,
}: {
  item: ShipmentPaymentItem
  shipmentId: string | undefined
}) {
  const actions = usePaymentActions(
    item.id,
    shipmentId,
    item.receiptTransactionId,
  )
  const [settleOpen, setSettleOpen] = useState(false)
  const [viewOpen, setViewOpen] = useState(false)
  const [commentOpen, setCommentOpen] = useState(false)
  return (
    <>
      <ActionGroup
        item={item}
        actions={actions}
        shipmentId={shipmentId}
        onSettle={() => setSettleOpen(true)}
        onView={() => setViewOpen(true)}
      />
      <PaymentMoreMenu
        item={item}
        actions={actions}
        shipmentId={shipmentId}
        onComment={() => setCommentOpen(true)}
      />
      <SettlePaymentModal
        open={settleOpen}
        onOpenChange={setSettleOpen}
        item={item}
        shipmentId={shipmentId}
      />
      <PaymentViewModal
        open={viewOpen}
        onOpenChange={setViewOpen}
        paymentId={item.id}
      />
      <PaymentCommentDialog
        open={commentOpen}
        onOpenChange={setCommentOpen}
        paymentId={item.id}
      />
    </>
  )
}
